import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotifications, NOTIFICATION_TYPES } from '../contexts/NotificationContext';

const Container = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  max-width: 400px;
  width: 100%;
  
  @media (max-width: 768px) {
    top: 10px;
    right: 10px;
    left: 10px;
    max-width: none;
  }
`;

const NotificationCard = styled(motion.div)`
  background: ${props => {
    switch (props.type) {
      case NOTIFICATION_TYPES.SUCCESS:
        return 'linear-gradient(135deg, rgba(40, 167, 69, 0.95), rgba(34, 139, 34, 0.95))';
      case NOTIFICATION_TYPES.ERROR:
        return 'linear-gradient(135deg, rgba(220, 53, 69, 0.95), rgba(178, 34, 34, 0.95))';
      case NOTIFICATION_TYPES.WARNING:
        return 'linear-gradient(135deg, rgba(255, 193, 7, 0.95), rgba(255, 140, 0, 0.95))';
      case NOTIFICATION_TYPES.INFO:
        return 'linear-gradient(135deg, rgba(33, 150, 243, 0.95), rgba(30, 144, 255, 0.95))';
      default:
        return 'linear-gradient(135deg, rgba(108, 117, 125, 0.95), rgba(73, 80, 87, 0.95))';
    }
  }};
  color: white;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  
  &:hover {
    transform: translateX(-5px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
  }
  
  @media (max-width: 768px) {
    padding: 0.8rem;
  }
`;

const NotificationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`;

const NotificationIcon = styled.div`
  font-size: 1.2rem;
  margin-right: 0.5rem;
  flex-shrink: 0;
`;

const NotificationContent = styled.div`
  flex: 1;
`;

const NotificationTitle = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.3rem;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const NotificationMessage = styled.div`
  font-size: 0.8rem;
  opacity: 0.9;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  opacity: 0.7;
  padding: 0;
  margin-left: 0.5rem;
  flex-shrink: 0;
  
  &:hover {
    opacity: 1;
  }
`;

const NotificationFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
  font-size: 0.7rem;
  opacity: 0.8;
`;

const NotificationAction = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 6px;
  font-size: 0.7rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
  }
`;

const NotificationTimestamp = styled.div`
  font-size: 0.6rem;
  opacity: 0.6;
`;

const NotificationContainer = () => {
  const { notifications, removeNotification } = useNotifications();

  const getNotificationIcon = (type) => {
    switch (type) {
      case NOTIFICATION_TYPES.SUCCESS:
        return '✅';
      case NOTIFICATION_TYPES.ERROR:
        return '❌';
      case NOTIFICATION_TYPES.WARNING:
        return '⚠️';
      case NOTIFICATION_TYPES.INFO:
        return 'ℹ️';
      default:
        return '🔔';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h`;
    return date.toLocaleDateString('es-MX');
  };

  return (
    <Container>
      <AnimatePresence>
        {notifications.map((notification) => (
          <NotificationCard
            key={notification.id}
            type={notification.type}
            initial={{ opacity: 0, x: 300, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 300, scale: 0.8 }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 30,
              opacity: { duration: 0.3 }
            }}
            onClick={() => {
              if (notification.onClick) {
                notification.onClick();
              }
            }}
          >
            <NotificationHeader>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <NotificationIcon>
                  {getNotificationIcon(notification.type)}
                </NotificationIcon>
                <NotificationContent>
                  {notification.title && (
                    <NotificationTitle>{notification.title}</NotificationTitle>
                  )}
                  <NotificationMessage>{notification.message}</NotificationMessage>
                </NotificationContent>
              </div>
              <CloseButton onClick={(e) => {
                e.stopPropagation();
                removeNotification(notification.id);
              }}>
                ×
              </CloseButton>
            </NotificationHeader>
            
            {(notification.action || notification.timestamp) && (
              <NotificationFooter>
                <div>
                  {notification.action && (
                    <NotificationAction onClick={(e) => {
                      e.stopPropagation();
                      notification.action.onClick();
                    }}>
                      {notification.action.label}
                    </NotificationAction>
                  )}
                </div>
                <NotificationTimestamp>
                  {formatTimestamp(notification.timestamp)}
                </NotificationTimestamp>
              </NotificationFooter>
            )}
          </NotificationCard>
        ))}
      </AnimatePresence>
    </Container>
  );
};

export default NotificationContainer;